// Portal del CLIENTE: la pantalla sin login a la que llevan los dos botones del
// correo de aceptación. Rutas PÚBLICAS: no dependen de la sesión, y el token de
// la URL es la única credencial.
// Ver `api/app/Http/Controllers/Api/PortalPublicoController.php`.

import { apiFetch } from "@/lib/api";

/** Un archivo que ya llegó por uno de los espacios. */
export interface ArchivoDelPortal {
  id: number;
  nombre: string;
  bytes: number;
  subido_en: string | null;
  /**
   * Si el cliente lo puede quitar. Lo decide el SERVIDOR y no se deduce acá:
   * la regla —solo lo que subió él, nunca lo que cargó el equipo— no puede
   * vivir en dos lugares. El endpoint la vuelve a verificar igual, pero
   * dibujar un botón que después responde 404 es prometer algo que no se
   * cumple.
   */
  puede_eliminarse: boolean;
}

/** Los tres espacios en que el cliente deja sus archivos. Es el que va en la URL. */
export type ClaveDeSeccionDelPortal = "legal" | "tasaciones" | "descriptiva";

/**
 * Un espacio de subida: el cliente deja ahí lo que corresponda sin tener que
 * saber qué documento es cada uno. Quién los clasifica es el equipo.
 */
export interface SeccionDelPortal {
  clave: ClaveDeSeccionDelPortal;
  nombre: string;
  /** Solo lo que mandó el cliente por este espacio. */
  archivos: ArchivoDelPortal[];
}

/** Los datos de facturación ya cargados. */
export interface FacturacionDelPortal {
  /**
   * La respuesta a "¿Requieres factura?": `false` es que el cliente contestó
   * que no al aceptar (la fila existe y trae todos los datos en `null`);
   * `true`, que sí; `null`, que nadie preguntó.
   */
  requiere_factura: boolean | null;
  rut: string | null;
  razon_social: string | null;
  giro: string | null;
  direccion: string | null;
  comuna_codigo: string | null;
  /** Nombre resuelto contra el maestro; el formulario manda el código. */
  comuna: string | null;
  email_facturacion: string | null;
  telefono: string | null;
  contacto_nombre: string | null;
  actualizado_en: string | null;
}

/**
 * Todo lo que el cliente ve en su portal.
 *
 * Es a propósito MUCHO más chico que `Informe`: sin id, sin número del ERP, sin
 * estado del proceso, sin precio y sin revisores. El backend tampoco los manda,
 * y este tipo es el que hace notar si algún día empiezan a llegar.
 */
export interface Portal {
  cliente: string | null;
  propiedad: string | null;
  ubicacion: string | null;
  /**
   * Lo pactado sobre la entrega, ya en palabras.
   *
   * Casi siempre un PLAZO ("15 días hábiles a contar de la visita de
   * inspección") y no un día del calendario: el plazo corre desde la visita,
   * que cuando el cliente entra acá todavía no ocurrió. Solo las cotizaciones
   * aceptadas bajo el modelo anterior traen una fecha.
   *
   * Por eso la pantalla NO puede envolverlo en un molde que asuma una fecha
   * ("para el {entrega}").
   */
  entrega: string | null;
  /** El plazo pelado ("15 días hábiles"), sin su disparador. */
  plazo: string | null;
  /** Siempre las tres, en este orden: legal, tasaciones, descriptiva. */
  secciones: SeccionDelPortal[];
  /**
   * Los nombres de los documentos legales que el presupuesto le pidió. Es
   * orientación para el cliente, no una lista de casillas por completar.
   */
  documentos_pedidos: string[];
  /**
   * `null` cuando el cliente todavía no los mandó, que NO es lo mismo que un
   * registro en blanco: la pantalla lo muestra como una tarea pendiente.
   */
  facturacion: FacturacionDelPortal | null;
  /**
   * El ejecutivo del informe. `nombre` y `email` siempre llegan —sin ejecutivo
   * asignado caen a los datos genéricos de Consultek, porque el cliente tiene
   * que poder responderle a alguien—, pero el teléfono NO tiene reemplazo: es
   * opcional en la ficha del usuario y no existe otro número que sustituya al
   * de esta persona. Con `null`, el pie omite la línea.
   */
  contacto: { nombre: string; email: string; telefono: string | null };
}

/** Lo que el formulario de facturación manda. */
export interface PayloadFacturacion {
  rut: string;
  razon_social: string;
  giro: string;
  direccion: string;
  comuna_codigo: string;
  email_facturacion: string;
  telefono: string | null;
  contacto_nombre: string | null;
}

const base = (token: string) => `/api/portal/${encodeURIComponent(token)}`;

export async function fetchPortal(token: string): Promise<Portal> {
  const respuesta = await apiFetch<{ data: Portal }>(base(token));
  return respuesta.data;
}

/**
 * Adjunta un archivo a uno de los tres espacios.
 *
 * `FormData` y POST, no PUT: PHP no parsea `multipart/form-data` en un PUT
 * —`$_FILES` llega vacío— y además cada llamada AGREGA un archivo en vez de
 * reemplazar el anterior.
 *
 * Las TRES escrituras devuelven el portal COMPLETO y no solo lo que cambió: el
 * hook siembra su cache con la respuesta, y una parcial no se leería como "no
 * vino" sino como "ya no está" — o sea, un archivo recién subido desapareciendo
 * de la lista.
 */
export async function subirArchivoRequest(
  token: string,
  seccion: ClaveDeSeccionDelPortal,
  archivo: File,
): Promise<Portal> {
  const cuerpo = new FormData();
  cuerpo.append("archivo", archivo);

  const respuesta = await apiFetch<{ data: Portal }>(
    `${base(token)}/secciones/${seccion}/archivos`,
    { method: "POST", body: cuerpo },
  );
  return respuesta.data;
}

export async function eliminarArchivoRequest(token: string, archivoId: number): Promise<Portal> {
  const respuesta = await apiFetch<{ data: Portal }>(`${base(token)}/archivos/${archivoId}`, {
    method: "DELETE",
  });
  return respuesta.data;
}

export async function guardarFacturacionRequest(
  token: string,
  payload: PayloadFacturacion,
): Promise<Portal> {
  const respuesta = await apiFetch<{ data: Portal }>(`${base(token)}/facturacion`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return respuesta.data;
}
